import useSWR from 'swr';

import useApi from '@hooks/useApi';

import { Organization } from '@models/Organization';
import { FeatureResponse } from '@models/Permission';
import { PermissionName } from '@models/Permission';
import { ProjectType } from '@models/Project';

import Endpoints from '@services/Endpoints';

import { hasPermission } from '@util/PermissionUtil';

import useAuth from './useAuth';

const fetchFeatures = async (api: any) => {
    const featureResponse: FeatureResponse = await api.get(Endpoints.organization.features());
    return featureResponse;
};

const useOrganizationPermissions = (organization?: Organization | null) => {
    const api = useApi();
    const { user, authLoading } = useAuth();

    const {
        data: features,
        error,
        isValidating,
    } = useSWR(user && !authLoading ? 'organization-features' : null, () => fetchFeatures(api), {
        revalidateOnFocus: false,
        dedupingInterval: 60_000, // Prevent multiple requests within 60 seconds
    });

    const check = (requires: PermissionName | PermissionName[], any = false) => {
        return hasPermission(organization, { requires, any });
    };

    const permissions = {
        canInviteMembers: check(PermissionName.invite_organization_users),
        canEditRoles: check(PermissionName.invite_organization_users),
        canRemoveMembers: check(PermissionName.remove_organization_users),
        canEdit: check(PermissionName.edit_organization),
        canCreateProjectExperiments: check(PermissionName.create_project_experiments),
        canCreateShareableExperiments: check(PermissionName.create_organization_shareable_experiments),
        canCreateTeams: check(PermissionName.create_organization_teams),
        canCreateApps: check(PermissionName.create_organization_teams),
        canEditApps: check(PermissionName.create_organization_teams),
        canCreateLiteratureCollections: check(PermissionName.create_organization_teams),
        canEditLiteratureCollections: check(PermissionName.create_organization_teams),
        canCreateGeoImports: check(PermissionName.create_organization_teams),
        canSubmitLettersOfSupport: check(PermissionName.create_organization_teams),
        canManageLiteratureLabels: check(PermissionName.create_organization_teams),
    };

    return {
        ...permissions,
        canAddExperimentForProjectType: (type?: ProjectType | string | null) => {
            return type === ProjectType.team
                ? permissions.canCreateProjectExperiments
                : permissions.canCreateShareableExperiments;
        },
        features,
        error,
        isLoading: isValidating,
    };
};

export default useOrganizationPermissions;
